import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import Modal from 'components/Modal/Modal';
import ProjectThumbnailCard from 'components/TextProjects/ProjectThumbnailCard/ProjectThumbnailCard';
import CropWorkspace from 'components/ImageCrop/CropWorkspace';
import {
  attachCropDialogChrome,
  PREVIEW_THROTTLE_MS,
  readFileAsDataUrl,
  resolveCropSaveImage,
  shouldApplyInitialCrop,
} from 'components/ImageCrop/cropModalUtils';
import { getAutomaticCoverCrop } from 'helpers/projectCoverAutoCrop';
import {
  PROJECT_CARD_ASPECT_RATIO,
  PROJECT_CARD_ASPECT_SQUARE,
} from 'helpers/projectCoverAspect';
import styles from './AdjustThumbnailModal.module.scss';

const TITLE_ID = 'AdjustThumbnailModal-title';
const DESC_ID = 'AdjustThumbnailModal-desc';
const ASPECT_GROUP_ID = 'AdjustThumbnailModal-aspect';

/**
 * Shared “Adjust thumbnail” modal for project image, video-override, and audio cover flows.
 * Crop frame is locked to the card aspect ratio — free-ratio cropping is not available.
 * Audio may opt into Square (1:1) vs Widescreen (16:9) via `allowAspectChoice`.
 */
const AdjustThumbnailModal = ({
  show,
  aspectRatio,
  allowAspectChoice,
  resetRemovesThumbnail,
  initialFile,
  initialSrc,
  initialCrop,
  projectTitle,
  projectDescription,
  firstRole,
  onCancel,
  onSave,
  onReplaceFile,
}) => {
  const dialogRef = useRef(null);
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null);
  const previousFocusRef = useRef(null);
  const appliedInitialCropRef = useRef(false);
  const previewTimerRef = useRef(null);
  const suppressSourceChangeRef = useRef(false);
  /** True after the user picks a replacement file in this modal session. */
  const userSelectedFileRef = useRef(false);
  /** After an aspect toggle, skip restoring the prior-ratio initialCrop. */
  const skipInitialCropRef = useRef(false);
  /** True after Square/Widescreen changes — save must persist the new-ratio crop. */
  const aspectChangedRef = useRef(false);
  /**
   * True after “Remove thumbnail” in resetRemovesThumbnail mode — Save must
   * call onSave(null) even if a replacement file was selected this session.
   */
  const removeRequestedRef = useRef(false);
  /** Canvas + crop box for the automatic cover crop (zoom/pan baseline). */
  const automaticSnapshotRef = useRef(null);

  const [src, setSrc] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(0.1);
  const [maxZoom, setMaxZoom] = useState(3);
  const [pendingSource, setPendingSource] = useState('custom');
  const [ready, setReady] = useState(false);
  const [activeAspectRatio, setActiveAspectRatio] = useState(aspectRatio);

  const isSquare = activeAspectRatio === PROJECT_CARD_ASPECT_SQUARE;
  const displayTitle = projectTitle || 'Untitled project';

  const getCropper = useCallback(() => {
    return cropperRef.current && cropperRef.current.cropper
      ? cropperRef.current.cropper
      : null;
  }, []);

  const updatePreview = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    try {
      const canvas = cropper.getCroppedCanvas({
        width: isSquare ? 240 : 320,
        height: isSquare ? 240 : 180,
        fillColor: '#ffffff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'low',
      });
      if (canvas) {
        setPreviewUrl(canvas.toDataURL('image/jpeg', 0.7));
      }
    } catch (err) {
      // Cross-origin taint — keep last preview if any.
    }

    try {
      const imageData = cropper.getImageData();
      if (imageData && imageData.naturalWidth) {
        const ratio = imageData.width / imageData.naturalWidth;
        setZoom(ratio);
      }
    } catch (err) {
      // ignore
    }
  }, [getCropper, isSquare]);

  const schedulePreview = useCallback(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }
    previewTimerRef.current = setTimeout(() => {
      previewTimerRef.current = null;
      updatePreview();
    }, PREVIEW_THROTTLE_MS);
  }, [updatePreview]);

  const syncZoomBounds = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    try {
      const imageData = cropper.getImageData();
      const canvasData = cropper.getCanvasData();
      if (!imageData || !imageData.naturalWidth || !canvasData) {
        return;
      }

      // Minimum zoom keeps the image covering the crop box (no letterboxing).
      const coverZoom = Math.max(
        canvasData.width / imageData.naturalWidth,
        canvasData.height / imageData.naturalHeight
      );
      const nextMin = Number.isFinite(coverZoom) ? coverZoom * 0.98 : 0.1;
      const nextMax = Math.max(nextMin * 4, 3);

      setMinZoom(nextMin);
      setMaxZoom(nextMax);
      setZoom(imageData.width / imageData.naturalWidth);
    } catch (err) {
      // ignore
    }
  }, [getCropper]);

  const captureAutomaticSnapshot = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    try {
      automaticSnapshotRef.current = {
        data: cropper.getData(),
        canvasData: cropper.getCanvasData(),
        cropBoxData: cropper.getCropBoxData(),
      };
    } catch (err) {
      automaticSnapshotRef.current = null;
    }
  }, [getCropper]);

  const applyAutomaticCrop = useCallback(() => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    suppressSourceChangeRef.current = true;

    const snapshot = automaticSnapshotRef.current;
    if (snapshot && snapshot.canvasData && snapshot.cropBoxData) {
      // Restore exact automatic zoom/pan + crop frame (not just crop coords).
      cropper.setCanvasData(snapshot.canvasData);
      cropper.setCropBoxData(snapshot.cropBoxData);
    } else {
      // Undo any user zoom/pan, then apply the centre-cover automatic crop.
      cropper.reset();

      const imageData = cropper.getImageData();
      const auto = getAutomaticCoverCrop(
        imageData.naturalWidth,
        imageData.naturalHeight,
        activeAspectRatio
      );

      if (auto) {
        cropper.setData({
          x: auto.x,
          y: auto.y,
          width: auto.width,
          height: auto.height,
        });
      }

      captureAutomaticSnapshot();
    }

    setPendingSource('auto');
    syncZoomBounds();
    updatePreview();

    // cropperjs emits crop events for setData/reset — don't flip source to custom.
    window.setTimeout(() => {
      suppressSourceChangeRef.current = false;
      syncZoomBounds();
      updatePreview();
    }, 0);
  }, [
    activeAspectRatio,
    captureAutomaticSnapshot,
    getCropper,
    syncZoomBounds,
    updatePreview,
  ]);

  const loadSource = useCallback(async (file, url) => {
    appliedInitialCropRef.current = false;
    skipInitialCropRef.current = false;
    automaticSnapshotRef.current = null;
    setReady(false);
    setPreviewUrl(null);

    if (file instanceof File || file instanceof Blob) {
      const dataUrl = await readFileAsDataUrl(file);
      setSrc(dataUrl);
      setOriginalImage(file);
      return;
    }

    if (url) {
      setSrc(url);
      setOriginalImage(url);
      return;
    }

    setSrc(null);
    setOriginalImage(null);
  }, []);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    userSelectedFileRef.current = false;
    skipInitialCropRef.current = false;
    aspectChangedRef.current = false;
    removeRequestedRef.current = false;
    // Capture opening aspect once per open. Parent session aspect may update
    // while closed; do not re-sync mid-edit if the parent re-renders.
    setActiveAspectRatio(aspectRatio);
    setPendingSource(initialCrop ? 'custom' : 'auto');
    loadSource(initialFile, initialSrc);

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- aspectRatio only on open via `show`
  }, [show, initialFile, initialSrc, initialCrop, loadSource]);

  // Focus trap + restore focus + arrow nudge
  useEffect(() => {
    if (!show) {
      return undefined;
    }

    return attachCropDialogChrome({
      dialogRef,
      getCropper,
      src,
      onNudge: () => {
        setPendingSource('custom');
        schedulePreview();
      },
      previousFocusRef,
    });
  }, [show, getCropper, schedulePreview, src]);

  const handleReady = () => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    setReady(true);

    // Always establish the automatic baseline first so Reset can restore zoom+crop.
    suppressSourceChangeRef.current = true;
    cropper.reset();

    const imageData = cropper.getImageData();
    const auto = getAutomaticCoverCrop(
      imageData.naturalWidth,
      imageData.naturalHeight,
      activeAspectRatio
    );

    if (auto) {
      cropper.setData({
        x: auto.x,
        y: auto.y,
        width: auto.width,
        height: auto.height,
      });
    }
    captureAutomaticSnapshot();

    const skipInitial = skipInitialCropRef.current;
    skipInitialCropRef.current = false;

    // Never re-apply a prior image's crop after the user picks a replacement,
    // or after an aspect toggle (coords belong to the previous ratio).
    if (
      !skipInitial &&
      shouldApplyInitialCrop(initialCrop, userSelectedFileRef.current) &&
      !appliedInitialCropRef.current
    ) {
      const { x, y, width, height } = initialCrop;
      appliedInitialCropRef.current = true;
      cropper.setData({
        x: x < 0 ? 0 : x,
        y: y < 0 ? 0 : y,
        width,
        height,
      });
      setPendingSource('custom');
    } else {
      setPendingSource('auto');
    }

    syncZoomBounds();
    updatePreview();

    window.setTimeout(() => {
      suppressSourceChangeRef.current = false;
      syncZoomBounds();
      updatePreview();
    }, 0);
  };

  const handleAspectSelect = (nextAspect) => {
    if (!allowAspectChoice || nextAspect === activeAspectRatio) {
      return;
    }

    // Remount cropper at the new ratio and re-run centre-cover auto crop.
    // Mark aspect changed so Save persists that crop instead of onSave(null).
    skipInitialCropRef.current = true;
    appliedInitialCropRef.current = false;
    automaticSnapshotRef.current = null;
    aspectChangedRef.current = true;
    removeRequestedRef.current = false;
    setReady(false);
    setPendingSource('auto');
    setActiveAspectRatio(nextAspect);
  };

  const handleCrop = () => {
    if (!suppressSourceChangeRef.current) {
      setPendingSource('custom');
    }
    schedulePreview();
  };

  const handleZoomSlider = (event) => {
    const cropper = getCropper();
    if (!cropper) {
      return;
    }

    const next = Number(event.target.value);
    if (!Number.isFinite(next)) {
      return;
    }

    cropper.zoomTo(next);
    setZoom(next);
    setPendingSource('custom');
    schedulePreview();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    userSelectedFileRef.current = true;
    removeRequestedRef.current = false;
    setPendingSource('auto');
    // Notify parent a replacement was picked; do not clear stored crop here —
    // that must wait until Save commits (cancel should keep the prior crop).
    if (typeof onReplaceFile === 'function') {
      onReplaceFile();
    }
    await loadSource(file, null);
  };

  const handleReset = () => {
    // Clear so Save can take onSave(null); leave userSelectedFileRef alone
    // for “Reset to automatic” (replacement-file auto-crop still persists).
    aspectChangedRef.current = false;
    if (resetRemovesThumbnail) {
      removeRequestedRef.current = true;
    }
    applyAutomaticCrop();
  };

  const handleSave = () => {
    // “Remove thumbnail”: persist removal while still on the auto crop, even
    // if a replacement file was selected. “Reset to automatic”: null only when
    // no replacement file / aspect change — otherwise keep that file’s auto crop.
    if (
      pendingSource === 'auto' &&
      ((resetRemovesThumbnail && removeRequestedRef.current) ||
        (!userSelectedFileRef.current && !aspectChangedRef.current))
    ) {
      onSave(null);
      return;
    }

    const cropper = getCropper();
    if (!cropper || !src) {
      return;
    }

    const data = cropper.getData(true);
    let naturalWidth;
    let naturalHeight;
    try {
      const imageData = cropper.getImageData();
      if (imageData && imageData.naturalWidth && imageData.naturalHeight) {
        naturalWidth = imageData.naturalWidth;
        naturalHeight = imageData.naturalHeight;
      }
    } catch (err) {
      // ignore
    }

    let croppedImage = null;
    try {
      const canvas = cropper.getCroppedCanvas({
        width: isSquare ? 240 : 320,
        height: isSquare ? 240 : 180,
        fillColor: '#ffffff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      croppedImage = canvas ? canvas.toDataURL('image/jpeg', 0.85) : null;
    } catch (err) {
      croppedImage = null;
    }

    onSave({
      x: data.x < 0 ? 0 : data.x,
      y: data.y < 0 ? 0 : data.y,
      width: data.width,
      height: data.height,
      image: resolveCropSaveImage({
        originalImage,
        initialSrc,
        userSelectedFile: userSelectedFileRef.current,
      }),
      croppedImage,
      naturalWidth,
      naturalHeight,
      aspectRatio: activeAspectRatio,
    });
  };

  const canSave = Boolean(src) && ready;

  return (
    <Modal
      show={show}
      onExit={onCancel}
      backgroundExit={false}
      closeButton={false}
      animation="scale"
      modalClassName={styles.modal}
      modalStyle={{
        width: 'calc(100% - 24px)',
        maxWidth: 880,
        boxSizing: 'border-box',
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        aria-describedby={DESC_ID}
        tabIndex={-1}
      >
        <header className={styles.header}>
          <h2 className={styles.title} id={TITLE_ID}>
            Adjust thumbnail
          </h2>
          <p className={styles.subhead} id={DESC_ID}>
            Choose what shows on portfolio cards and in client searches.
          </p>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCancel}
            aria-label="Cancel"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        <div className={styles.body}>
          <div className={styles.workspaceColumn}>
            {allowAspectChoice ? (
              <div
                className={styles.aspectToggle}
                role="group"
                aria-labelledby={ASPECT_GROUP_ID}
              >
                <span className={styles.aspectLabel} id={ASPECT_GROUP_ID}>
                  Crop shape
                </span>
                <div className={styles.aspectSeg}>
                  <button
                    type="button"
                    className={`${styles.aspectOption}${
                      isSquare ? ` ${styles.aspectOptionActive}` : ''
                    }`}
                    aria-pressed={isSquare}
                    onClick={() =>
                      handleAspectSelect(PROJECT_CARD_ASPECT_SQUARE)
                    }
                  >
                    Square
                  </button>
                  <button
                    type="button"
                    className={`${styles.aspectOption}${
                      !isSquare ? ` ${styles.aspectOptionActive}` : ''
                    }`}
                    aria-pressed={!isSquare}
                    onClick={() =>
                      handleAspectSelect(PROJECT_CARD_ASPECT_RATIO)
                    }
                  >
                    Widescreen
                  </button>
                </div>
              </div>
            ) : null}

            <CropWorkspace
              src={src}
              cropperRef={cropperRef}
              aspectRatio={activeAspectRatio}
              maskShape="rect"
              cropBoxResizable
              onReady={handleReady}
              onCrop={handleCrop}
              onZoom={schedulePreview}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={maxZoom}
              onZoomChange={handleZoomSlider}
              showZoom
              emptyTitle="Upload an image to crop your thumbnail."
              emptyHint=""
              onEmptyClick={() =>
                fileInputRef.current && fileInputRef.current.click()
              }
              fileInputRef={fileInputRef}
              onFileChange={handleFileChange}
              hint="Drag to reposition · pinch or scroll to zoom"
            />
          </div>

          <div className={styles.previewColumn}>
            <p className={styles.previewEyebrow}>Preview</p>

            <ProjectThumbnailCard
              imageUrl={previewUrl}
              title={displayTitle}
              description={projectDescription}
              firstRole={firstRole}
              cropSource={pendingSource}
              square={isSquare}
              width={isSquare ? 240 : 280}
            />

            <div className={styles.searchPreview}>
              <div
                className={`${styles.searchThumb}${
                  isSquare ? ` ${styles.square}` : ''
                }`}
              >
                {previewUrl ? <img src={previewUrl} alt="" /> : null}
              </div>
              <div className={styles.searchMeta}>
                <p className={styles.searchTitle}>{displayTitle}</p>
                <p className={styles.searchCaption}>
                  {isSquare ? 'Browse / music row size' : 'Search result size'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerLeft}>
            <button
              type="button"
              className={
                resetRemovesThumbnail ? styles.removeLink : styles.resetLink
              }
              onClick={handleReset}
              disabled={!src || !ready}
            >
              {resetRemovesThumbnail
                ? 'Remove thumbnail'
                : 'Reset to automatic'}
            </button>
          </div>
          <div className={styles.footerRight}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.saveButton}
              onClick={handleSave}
              disabled={!canSave}
            >
              Save thumbnail
            </button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};

AdjustThumbnailModal.propTypes = {
  show: PropTypes.bool.isRequired,
  aspectRatio: PropTypes.number.isRequired,
  /** When true (audio cover art), show Square / Widescreen crop toggle. */
  allowAspectChoice: PropTypes.bool,
  /**
   * When true (existing custom audio cover), the footer reset control reads as
   * “Remove thumbnail” and uses danger styling — saving then restores the
   * generated card.
   */
  resetRemovesThumbnail: PropTypes.bool,
  initialFile: PropTypes.object,
  initialSrc: PropTypes.string,
  initialCrop: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  projectTitle: PropTypes.string,
  projectDescription: PropTypes.string,
  firstRole: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  /** Called with crop details for a custom crop, or `null` to persist automatic. */
  onSave: PropTypes.func.isRequired,
  /** Fired when the user picks a replacement image in this modal session. */
  onReplaceFile: PropTypes.func,
};

AdjustThumbnailModal.defaultProps = {
  allowAspectChoice: false,
  resetRemovesThumbnail: false,
  initialFile: null,
  initialSrc: null,
  initialCrop: null,
  projectTitle: '',
  projectDescription: '',
  firstRole: '',
  onReplaceFile: null,
};

export default AdjustThumbnailModal;
